import { UIElement, TransformOptions } from "./ui_element.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { ImgID } from "../../image_loader/img_id.js";
import { NineSliceDrawer, CornerSizes } from "../../image_loader/nine_slicer/nine_slice_drawer.js";

export enum MirrorMode
{
  horizontal = "horizontal", // ліво↔право (вертикальна вісь симетрії)
  vertical   = "vertical",   // верх↕низ (горизонтальна вісь симетрії)
  both       = "both",       // 4 квадранти
}

export enum SourceKind
{
  none       = "none",
  image      = "image",
  nine_slice = "nine_slice",
}

export class UIMirrorElement extends UIElement
{
  private source_kind: SourceKind = SourceKind.none;

  private img_id?: ImgID;

  private ns_img_id?: ImgID;
  private ns_key?: string;
  private mirror_mode: MirrorMode = MirrorMode.horizontal;
  private ns_corner_size: CornerSizes = {
    top_left:     { width: 10, height: 10 },
    bottom_right: { width: 10, height: 10 },
  };


  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    mirror_mode: MirrorMode,
    options?: TransformOptions,
  )
  {
    super( ctx, x, y, width, height, false, false, options );
    this.mirror_mode = mirror_mode
  }

  public setImageSource( img_id: ImgID ): void
  {
    this.source_kind = SourceKind.image;
    this.img_id = img_id;
  }

  public setNineSliceSource(
    img_id: ImgID,
    ns_key: string,
    corner_size: CornerSizes = {
      top_left: { width: 0.2, height: 0.2 },
      bottom_right: { width: 0.2, height: 0.2 },
    }
  ): void
  {
    this.source_kind = SourceKind.nine_slice;
    this.ns_img_id = img_id;
    this.ns_key = ns_key;
    this.ns_corner_size = corner_size;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() || this.source_kind === "none" ) return;

    this.startRender();

    const { x0, y0, w, h, qw, qh } = this.getLocalRect();

    this.applyTransform();

    switch ( this.mirror_mode )
    {
      case MirrorMode.horizontal:
        this.renderHorizontal( x0, y0, w, h );
        break;

      case MirrorMode.vertical:
        this.renderVertical( x0, y0, w, h );
        break;

      case MirrorMode.both:
        this.renderBoth( x0, y0, qw, qh );
        break;
    }

    this.finishRender();
  }

  private getLocalRect(): { x0: number; y0: number; w: number; h: number; qw: number; qh: number }
  {
    const w = this.rect.getW();
    const h = this.rect.getH();
    const x0 = -this.rect.getW2();
    const y0 = -this.rect.getH2();
    return { x0, y0, w, h, qw: w * 0.5, qh: h * 0.5 };
  }

  private withMirror(
    flip_x: boolean,
    flip_y: boolean,
    axis_x: number,
    axis_y: number,
    draw: () => void,
  ): void
  {
    this.ctx.save();

    if ( flip_x ) this.ctx.translate( 2 * axis_x, 0 );
    if ( flip_y ) this.ctx.translate( 0, 2 * axis_y );

    this.ctx.scale( flip_x ? -1 : 1, flip_y ? -1 : 1 );
    draw();

    this.ctx.restore();
  }

  private renderHorizontal( x0: number, y0: number, w: number, h: number ): void
  {
    const half_w = w * 0.5;
    const axis_x = x0 + half_w;

    this.draw_source( x0, y0, half_w, h );

    this.withMirror( true, false, axis_x, 0, () =>
    {
      this.draw_source( x0, y0, half_w, h );
    } );
  }

  private renderVertical( x0: number, y0: number, w: number, h: number ): void
  {
    const half_h = h * 0.5;
    const axis_y = y0 + half_h;

    this.draw_source( x0, y0, w, half_h );

    this.withMirror( false, true, 0, axis_y, () =>
    {
      this.draw_source( x0, y0, w, half_h );
    } );
  }

  private renderBoth( x0: number, y0: number, qw: number, qh: number ): void
  {
    const axis_x = x0 + qw;
    const axis_y = y0 + qh;

    this.draw_source( x0, y0, qw, qh );

    this.withMirror( true, false, axis_x, 0, () =>
    {
      this.draw_source( x0, y0, qw, qh );
    } );

    this.withMirror( false, true, 0, axis_y, () =>
    {
      this.draw_source( x0, y0, qw, qh );
    } );

    this.withMirror( true, true, axis_x, axis_y, () =>
    {
      this.draw_source( x0, y0, qw, qh );
    } );
  }

  public name(): string
  {
    return "UIMirrorElement";
  }

  private draw_source( x: number, y: number, w: number, h: number ): void
  {
    switch ( this.source_kind )
    {
      case SourceKind.image:
        {
          if ( this.img_id === undefined ) return;
          const img = ImagesHolder.getImage( this.img_id );
          this.ctx.drawImage( img, x, y, w, h );
          break;
        }
      case SourceKind.nine_slice:
        {
          if ( !this.ns_img_id || !this.ns_key ) return;
          NineSliceDrawer.drawNineSliceImage(
            this.ctx,
            ImagesHolder.getImage( this.ns_img_id ),
            this.ns_key,
            x, y, w, h,
            {
              top_left: this.ns_corner_size.top_left,
              bottom_right: this.ns_corner_size.bottom_right,
            }
          );
          break;
        }
      default:
        break;
    }
  }
}